import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/authStore.js';
import { applySessionResultToCaches, invalidateProgress } from '../progress/hooks.js';
import { usePendingExamTaskStore } from './pendingExamTaskStore.js';
import { usePendingExamResultStore } from './pendingExamResultStore.js';
import { completeExamTaskDirect, saveExamResultDirect, EXAM_RESULTS_KEY } from './hooks.js';
import {
  useQueueFlusher,
  drainEntries,
  isEligible,
  RETENTION_MS,
  SESSION_GRACE_MS,
  type FlushController,
} from '../lib/useQueueFlusher.js';

/**
 * Mount once at the top of the authed tree (next to the other flushers).
 * Drains finished exam tasks that could not be sent at the time into
 * `complete_exam_task` (idempotent per attempt + task). Retries never give
 * up but back off per entry — see `useQueueFlusher`.
 */
export function ExamFlusher() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const tasks = usePendingExamTaskStore((s) => s.tasks);
  const remove = usePendingExamTaskStore((s) => s.remove);
  const markAttempted = usePendingExamTaskStore((s) => s.markAttempted);
  const pruneOlderThan = usePendingExamTaskStore((s) => s.pruneOlderThan);

  const flush = useCallback(
    async (ctl: FlushController) => {
      if (!user) return;
      const now = Date.now();
      pruneOlderThan(now - RETENTION_MS);
      const mine = tasks.filter(
        (t) =>
          t.userId === user.id &&
          isEligible(t, now) &&
          // Give the direct send time to land first.
          now - t.queuedAt >= SESSION_GRACE_MS,
      );
      await drainEntries({
        entries: mine,
        send: (e) => completeExamTaskDirect({ attemptId: e.attempt_id, test: e.test, task: e.task, correct: e.correct }),
        onSent: (e, result) => {
          remove('tasks', [e.id]);
          applySessionResultToCaches(qc, user.id, result);
          invalidateProgress(qc, user.id);
        },
        onFailed: (e) => markAttempted('tasks', [e.id], now),
        cancelled: ctl.cancelled,
      });
    },
    [user, tasks, remove, markAttempted, pruneOlderThan, qc],
  );

  useQueueFlusher(user ? flush : null);
  return null;
}

/**
 * Mount next to ExamFlusher. Drains finished sittings that could not be
 * sent at the time into `save_exam_result` (idempotent per attempt), then
 * refetches the account's results. Same retry policy as the task queue.
 */
export function ExamResultFlusher() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const results = usePendingExamResultStore((s) => s.results);
  const remove = usePendingExamResultStore((s) => s.remove);
  const markAttempted = usePendingExamResultStore((s) => s.markAttempted);
  const pruneOlderThan = usePendingExamResultStore((s) => s.pruneOlderThan);

  const flush = useCallback(
    async (ctl: FlushController) => {
      if (!user) return;
      const now = Date.now();
      pruneOlderThan(now - RETENTION_MS);
      const mine = results.filter(
        (r) => r.userId === user.id && isEligible(r, now) && now - r.queuedAt >= SESSION_GRACE_MS,
      );
      await drainEntries({
        entries: mine,
        send: (e) => saveExamResultDirect(e.payload),
        onSent: (e) => {
          remove('results', [e.id]);
          void qc.invalidateQueries({ queryKey: EXAM_RESULTS_KEY(user.id) });
        },
        onFailed: (e) => markAttempted('results', [e.id], now),
        cancelled: ctl.cancelled,
      });
    },
    [user, results, remove, markAttempted, pruneOlderThan, qc],
  );

  useQueueFlusher(user ? flush : null);
  return null;
}
